import { Skeleton } from "@/shared/ui/skeleton";
import { QueryErrorBanner } from "@/shared/ui/QueryErrorBanner";
import { cn } from "@/shared/lib/utils";

import type { ActivityItem, ActivityTone } from "../../api/types";
import { monthDayTime } from "../../model/siteLabels";

/** 色只按 tone 走,不按 kind(kind 是开放取值,后端加一种前端不该跟着改)。 */
const TONE_DOT: Record<ActivityTone, string> = {
  ok: "bg-success",
  warn: "bg-warning",
  info: "bg-muted-foreground/50",
};

/**
 * 「发布动态」(屏 1 右栏):按后端给的顺序渲染,前端不重排。
 * `items === null` 且无错 = 还在拉;拉不到在这一栏自己报,不拖垮左边的站点卡。
 * `id="activity"`:壳上「发布记录」入口指向的锚点。
 */
export function ActivityFeed({
  siteName,
  items,
  error,
  onRetry,
}: {
  /** 动态是站点级的,标题里点名是哪一站的,免得多站时被读成整个舱的。 */
  siteName: string;
  items: ActivityItem[] | null;
  error: string | null;
  onRetry: () => void;
}) {
  return (
    <section id="activity" aria-labelledby="website-activity-title" className="space-y-3">
      <h2 id="website-activity-title" className="text-sm font-semibold text-foreground">
        发布动态 · {siteName}
      </h2>
      {error ? (
        <QueryErrorBanner isError message={error} onRetry={onRetry} />
      ) : items === null ? (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-4/5" />
        </div>
      ) : items.length === 0 ? (
        <p className="text-xs text-muted-foreground">还没有发布动态。</p>
      ) : (
        <ol className="space-y-3">
          {items.map((it, i) => {
            const when = monthDayTime(it.at);
            return (
              <li key={`${it.at}-${i}`} className="flex gap-2.5">
                {/* cyber 皮肤的脉冲环定位已收进 skin-cyber.css 的 `.rounded-full.bg-success/-destructive/-warning`
                    共用规则(0925 收尾波 A4),这里不再本地补 `relative`。 */}
                <span
                  aria-hidden
                  data-activity-dot={it.tone}
                  className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", TONE_DOT[it.tone])}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline justify-between gap-x-2">
                    <span className="text-sm font-medium text-foreground">{it.title}</span>
                    {when && (
                      <time dateTime={it.at} className="text-2xs text-muted-foreground">
                        {when}
                      </time>
                    )}
                  </div>
                  {it.detail && <p className="text-xs text-muted-foreground">{it.detail}</p>}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
